import { observable, runInAction } from 'mobx'

import { Clock, Logger } from '../ports'
import { Contact } from '../protocol/types'
import { describeChatError, fold } from '../result'
import { ChatApi } from '../transport/api'

const STALE_AFTER_MS = 15 * 60 * 1000

interface CacheEntry {
  readonly contact: Contact | null
  readonly fetchedAt: number
}

export class ContactsCache {
  private entries = observable.map<string, CacheEntry>()
  private readonly inFlight = new Set<string>()

  public constructor(
    private readonly api: ChatApi,
    private readonly clock: Clock,
    private readonly logger: Logger,
  ) {}

  public get(id: string): Contact | undefined {
    const entry = this.entries.get(id)
    return entry?.contact ?? undefined
  }

  public getFullName(id: string): string | undefined {
    return this.get(id)?.fullName
  }

  public isResolved(id: string): boolean {
    return this.entries.has(id)
  }

  public async ensureLoaded(ids: readonly string[]): Promise<void> {
    const now = this.clock()
    const missing = [
      ...new Set(
        ids.filter(id => {
          if (this.inFlight.has(id)) {
            return false
          }
          const entry = this.entries.get(id)
          return !entry || now - entry.fetchedAt > STALE_AFTER_MS
        }),
      ),
    ]

    if (!missing.length) {
      return
    }

    missing.forEach(id => this.inFlight.add(id))
    try {
      const result = await this.api.getContactsByIds(missing)
      fold(
        result,
        contacts => {
          const fetchedAt = this.clock()
          const byId = new Map(contacts.map(c => [c.id, c]))
          runInAction(() => {
            missing.forEach(id =>
              this.entries.set(id, {
                contact: byId.get(id) ?? null,
                fetchedAt,
              }),
            )
          })
        },
        error => this.logger.warn(`contacts load: ${describeChatError(error)}`),
      )
    } finally {
      missing.forEach(id => this.inFlight.delete(id))
    }
  }
}
